import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import moment from 'moment';
import './BrandOnboardingTable.scss';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/pro-regular-svg-icons';

import { StatusChip, CodeChip, OwnerChip } from './BrandOnboardingChips';
import { copyToClipboard, getStalledDetail, isCodeStalled } from './brandOnboardingHelpers';
import { NETWORK_LABELS } from './brandOnboardingFieldConfig';

const BrandOnboardingTable = props => {
  const { isInitialLoad, visibleBrandRegistrationCodes, selectedBrandRegistrationCodeId, isSearching, emptyMessage } = props;
  const { recipientNamesById, allAdminIds, onSelectCode, onFilterByOwner, showLoadMore, onLoadMore } = props;

  const renderRow = code => {
    const isStalled = isCodeStalled(code);
    const isKnownRecipient = allAdminIds.has(code.NotificationsUser_id);
    const createdAtLabel = moment(code.createdAt).format('MMM D, YYYY');
    return (
      <div
        className={cn('brand-onboarding-row', { selected: code.id === selectedBrandRegistrationCodeId })}
        key={code.id}
        tabIndex={0}
        onClick={() => onSelectCode(code.id)}
        onKeyDown={e => {
          if (isSearching) return;
          if (e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault();
          onSelectCode(code.id);
        }}
      >
        <div className='cell lg'>
          <div className='brand-onboarding-row-name'>{code.brand?.name || code.name}</div>
          <div className='brand-onboarding-row-domain'>{code.brand?.domain || code.domain}</div>
        </div>
        <div className='cell md'>
          <CodeChip code={code.code} onCopy={() => copyToClipboard(code.code)} />
        </div>
        <div className='cell sm muted'>{NETWORK_LABELS[code.force_merchant] || code.force_merchant || '—'}</div>
        <div className='cell md'>
          {code.NotificationsUser_id ? (
            <OwnerChip
              userId={code.NotificationsUser_id}
              name={recipientNamesById.get(code.NotificationsUser_id)}
              isKnown={isKnownRecipient}
              title={`Filter by ${recipientNamesById.get(code.NotificationsUser_id)}`}
              onClick={e => {
                e.stopPropagation();
                onFilterByOwner(code.NotificationsUser_id);
              }}
            />
          ) : (
            <span className='brand-onboarding-owner-unassigned' title='No personal recipient — updates go only to #brand-onboarding-updates'>
              —
            </span>
          )}
        </div>
        <div
          className={cn('cell sm age', { stalled: isStalled })}
          title={isStalled ? `${createdAtLabel} — ${getStalledDetail(code)}` : createdAtLabel}
        >
          {isStalled && <FontAwesomeIcon icon={faExclamationTriangle} />}
          {moment(code.createdAt).fromNow(true)}
        </div>
        <div className='cell md'>
          <StatusChip registrationCode={code} />
        </div>
      </div>
    );
  };

  return (
    <div className='brand-onboarding-table-outer-container'>
      <div className={cn('brand-onboarding-table-inner-container', { isSearching })}>
        <div className='brand-onboarding-row header'>
          <div className='cell lg'>Brand</div>
          <div className='cell md'>Code</div>
          <div className='cell sm'>Network</div>
          <div className='cell md'>Owner</div>
          <div className='cell sm'>Age</div>
          <div className='cell md'>Status</div>
        </div>

        {isInitialLoad ? (
          new Array(12).fill(0).map((_unused, i) => (
            <div className='brand-onboarding-row skeleton' key={i}>
              <div className='cell lg'></div>
              <div className='cell md'></div>
              <div className='cell sm'></div>
              <div className='cell md'></div>
              <div className='cell sm'></div>
              <div className='cell md'></div>
            </div>
          ))
        ) : !visibleBrandRegistrationCodes.length ? (
          <div className='no-results'>{emptyMessage}</div>
        ) : (
          visibleBrandRegistrationCodes.map(renderRow)
        )}

        {showLoadMore && (
          <div className='brand-onboarding-load-more-container'>
            <button className='brand-onboarding-load-more-button' onClick={onLoadMore}>
              {isSearching ? 'Loading...' : 'Load More'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

BrandOnboardingTable.propTypes = {
  isInitialLoad: PropTypes.bool.isRequired,
  visibleBrandRegistrationCodes: PropTypes.array,
  selectedBrandRegistrationCodeId: PropTypes.number,
  isSearching: PropTypes.bool.isRequired,
  emptyMessage: PropTypes.string.isRequired,
  recipientNamesById: PropTypes.object.isRequired,
  allAdminIds: PropTypes.object.isRequired,
  onSelectCode: PropTypes.func.isRequired,
  onFilterByOwner: PropTypes.func.isRequired,
  showLoadMore: PropTypes.bool.isRequired,
  onLoadMore: PropTypes.func.isRequired
};

export default BrandOnboardingTable;
